import { pickBy } from 'lodash';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface UnsavedChangesState {
    unsavedKeys: Record<string, boolean>;
    unsavedTextByKey: Record<string, { title?: string; text?: string }>;
    activeUnsavedTextKey: string | null;
    hasUnsavedChanges: boolean;
    isNavigationBlocked: boolean;
    pendingAction: (() => void) | null;
    pendingCancelAction: (() => void) | null;
    _setHasUnsavedForKey: (key: string, hasChanges: boolean, options?: { title?: string; text?: string }) => void;
    currentDialogCopy: () => { title?: string; text?: string };
    requestNavigation: (action: () => void, onCancel?: () => void) => void;
    confirmNavigation: () => void;
    cancelNavigation: () => void;
}

export const useUnsavedChangesStore = create<UnsavedChangesState>()(
    devtools(
        (set, get) => ({
            unsavedKeys: {},
            unsavedTextByKey: {},
            activeUnsavedTextKey: null,
            hasUnsavedChanges: false,
            isNavigationBlocked: false,
            pendingAction: null,
            pendingCancelAction: null,
            _setHasUnsavedForKey: (key, hasChanges, options) => {
                const hasCustomCopy = Boolean(options?.title || options?.text);
                const unsavedKeys = pickBy({ ...get().unsavedKeys, [key]: hasChanges }, Boolean);
                const nextUnsavedTextByKey = { ...get().unsavedTextByKey };

                if (hasChanges) {
                    if (options?.title || options?.text) {
                        nextUnsavedTextByKey[key] = {
                            title: options.title,
                            text: options.text,
                        };
                    }
                } else {
                    delete nextUnsavedTextByKey[key];
                }

                let activeUnsavedTextKey = get().activeUnsavedTextKey;

                if (hasChanges && hasCustomCopy) {
                    activeUnsavedTextKey = key;
                } else if (!hasChanges && activeUnsavedTextKey === key) {
                    activeUnsavedTextKey =
                        Object.keys(unsavedKeys).find((unsavedKey) => Boolean(nextUnsavedTextByKey[unsavedKey])) ??
                        null;
                }

                set({
                    unsavedKeys,
                    unsavedTextByKey: nextUnsavedTextByKey,
                    activeUnsavedTextKey,
                    hasUnsavedChanges: Object.values(unsavedKeys).some(Boolean),
                });
            },
            currentDialogCopy: () => {
                const { unsavedKeys, unsavedTextByKey, activeUnsavedTextKey } = get();

                if (activeUnsavedTextKey && unsavedKeys[activeUnsavedTextKey]) {
                    return unsavedTextByKey[activeUnsavedTextKey] ?? {};
                }

                return {};
            },
            requestNavigation: (action, onCancel) => {
                if (get().hasUnsavedChanges) {
                    set({ isNavigationBlocked: true, pendingAction: action, pendingCancelAction: onCancel ?? null });
                } else {
                    action();
                }
            },
            confirmNavigation: () => {
                const { pendingAction } = get();
                set({ isNavigationBlocked: false, pendingAction: null, pendingCancelAction: null });
                pendingAction?.();
            },
            cancelNavigation: () => {
                const { pendingCancelAction } = get();
                set({ isNavigationBlocked: false, pendingAction: null, pendingCancelAction: null });
                pendingCancelAction?.();
            },
        }),
        { name: 'unsavedChanges' }
    )
);
